import { useCallback, useEffect, useId, useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Link,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import { useTranslation } from 'react-i18next';
import { auditService, type AuditSession } from '@services/auditService';
import ScrollableTableContainer from '@components/common/ScrollableTableContainer';
import { toDisplayError } from '@utils/apiError';
import { formatDate } from '@utils/format';

/**
 * Roles allowed to start an audit. Mirrors the API:
 * POST /organizations/:orgId/contexts/:contextId/audits is requireOrgRole('manager'),
 * and owner/admin outrank manager.
 */
const AUDIT_STARTING_ROLES = ['owner', 'admin', 'manager'];

/** The list endpoint's maximum page size. */
const LIST_LIMIT = 100;

const STATUS_COLOR: Record<AuditSession['status'], 'info' | 'warning' | 'success'> = {
  in_progress: 'info',
  finalized: 'warning',
  locked: 'success',
};

interface AuditsTabProps {
  orgId: string;
  /** The tracked place's real UUID — the audit endpoints reject a slug. */
  contextId: string;
  slug: string;
  /** The caller's role in the organization, or null when it is not known. */
  orgRole: string | null;
}

/**
 * REV-929: the audits for one tracked place, and the way in to starting one.
 *
 * Before this, the attestation flow (platform invariant 3: humans attest) was
 * reachable only by typing /contexts/:id/audits/new into the address bar.
 */
function AuditsTab({ orgId, contextId, slug, orgRole }: AuditsTabProps) {
  const { t } = useTranslation();
  const explanationId = useId();

  const [audits, setAudits] = useState<AuditSession[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const canStart = Boolean(orgRole && AUDIT_STARTING_ROLES.includes(orgRole));

  const fetchAudits = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await auditService.listAudits(orgId, contextId, { limit: LIST_LIMIT });
      setAudits(res.data);
      setTotal(res.pagination.total);
    } catch (err) {
      setError(toDisplayError(err, t('trackedPlaces.audits.errors.loadFailed')));
    } finally {
      setLoading(false);
    }
  }, [orgId, contextId, t]);

  useEffect(() => {
    void fetchAudits();
  }, [fetchAudits]);

  const startLabel = t('trackedPlaces.audits.start');

  return (
    <Card>
      <CardContent>
        <Box
          sx={{
            display: 'flex',
            flexWrap: 'wrap',
            gap: 2,
            justifyContent: 'space-between',
            alignItems: 'flex-start',
            mb: 2,
          }}
        >
          <Box>
            <Typography variant="h6" component="h2">
              {t('trackedPlaces.audits.title')}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {t('trackedPlaces.audits.description')}
            </Typography>
          </Box>
          <Box>
            {canStart ? (
              // It navigates, so it is a link (CLAUDE.md, links vs buttons).
              <Button
                component={RouterLink}
                to={`/orgs/${slug}/contexts/${contextId}/audits/new`}
                variant="contained"
                startIcon={<AddIcon aria-hidden="true" />}
              >
                {startLabel}
              </Button>
            ) : (
              <>
                {/* Unavailable, so it goes nowhere and is a button, not a link.
                    aria-disabled rather than `disabled`, which would take it out
                    of the tab order and put the explanation out of reach. The
                    explanation sits OUTSIDE the control so it does not become
                    part of its accessible name. */}
                <Button
                  variant="contained"
                  aria-disabled="true"
                  aria-describedby={explanationId}
                  startIcon={<AddIcon aria-hidden="true" />}
                  disableRipple
                  sx={{
                    // REV-681: aria-disabled has no visual effect in MUI on its own.
                    '&[aria-disabled="true"]': {
                      bgcolor: 'action.disabledBackground',
                      color: 'text.secondary',
                      boxShadow: 'none',
                      cursor: 'not-allowed',
                      '&:hover': { bgcolor: 'action.disabledBackground', boxShadow: 'none' },
                    },
                  }}
                >
                  {startLabel}
                </Button>
                <Typography
                  id={explanationId}
                  variant="body2"
                  color="text.secondary"
                  sx={{ mt: 1, maxWidth: 320 }}
                >
                  {t('trackedPlaces.audits.startUnavailable')}
                </Typography>
              </>
            )}
          </Box>
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress aria-label={t('trackedPlaces.audits.loading')} />
          </Box>
        ) : audits.length === 0 ? (
          !error && (
            <Typography color="text.secondary" sx={{ py: 2 }}>
              {t('trackedPlaces.audits.empty')}
            </Typography>
          )
        ) : (
          <>
            <ScrollableTableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>{t('trackedPlaces.audits.columns.name')}</TableCell>
                    <TableCell>{t('trackedPlaces.audits.columns.standard')}</TableCell>
                    <TableCell>{t('trackedPlaces.audits.columns.status')}</TableCell>
                    <TableCell>{t('trackedPlaces.audits.columns.started')}</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {audits.map((audit) => (
                    <TableRow key={audit.id} hover>
                      <TableCell>
                        <Link component={RouterLink} to={`/orgs/${slug}/audits/${audit.id}`}>
                          {audit.name || t('trackedPlaces.audits.untitled')}
                        </Link>
                      </TableCell>
                      <TableCell>
                        {t('trackedPlaces.audits.standard', {
                          version: audit.wcag_version,
                          level: audit.target_level,
                        })}
                      </TableCell>
                      <TableCell>
                        <Chip
                          size="small"
                          variant="outlined"
                          color={STATUS_COLOR[audit.status]}
                          label={t(`trackedPlaces.audits.status.${audit.status}`)}
                        />
                      </TableCell>
                      <TableCell>{formatDate(audit.createdAt)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </ScrollableTableContainer>
            {total > audits.length && (
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                {t('trackedPlaces.audits.truncated', { shown: audits.length, total })}
              </Typography>
            )}
          </>
        )}
      </CardContent>
    </Card>
  );
}

export default AuditsTab;
